/**
 * How a stream's state reads in the UI: pill tone, label, which button it offers, and a sentence for why it stopped.
 * Pure, so the table, the detail page and the tests all agree.
 */
import type { ActualState, DesiredState, ExitReason, Platform } from "@sp/shared/enums";

export type Tone = "ok" | "info" | "warn" | "bad" | "idle";

export const STATE_TONE: Record<ActualState, Tone> = {
  PENDING: "info",
  STARTING: "info",
  RUNNING: "ok",
  RETRYING: "warn",
  STOPPING: "idle",
  STOPPED: "idle",
  FAILED: "bad",
};

/**
 * The state to show. Right after Start (or Stop) the worker hasn't reacted yet, so the stored state still says
 * STOPPED (or RUNNING); show that the request is on its way instead of a state that contradicts the button.
 */
export function displayState(s: { desiredState: DesiredState; actualState: ActualState }): ActualState {
  if (s.desiredState === "RUNNING" && (s.actualState === "STOPPED" || s.actualState === "FAILED")) return "PENDING";
  if (s.desiredState === "STOPPED" && (s.actualState === "RUNNING" || s.actualState === "STARTING" || s.actualState === "RETRYING")) return "STOPPING";
  return s.actualState;
}

/** Settings can change only while the stream is stopped or failed (API rule, STREAM_NOT_EDITABLE). */
export const isEditable = (s: { actualState: ActualState }) => s.actualState === "STOPPED" || s.actualState === "FAILED";

/** Start when the user's intent is "stopped", Stop when it is "running", whatever the worker is doing right now. */
export const primaryAction = (s: { desiredState: DesiredState }): "start" | "stop" => (s.desiredState === "RUNNING" ? "stop" : "start");

export const PLATFORM_LABEL: Record<Platform, string> = {
  youtube: "YouTube",
  facebook: "Facebook",
  twitch: "Twitch",
  custom_rtmp: "Custom RTMP",
};
/** In menu order. (The runtime list in @sp/shared/enums would pull zod into the bundle.) */
export const PLATFORMS = Object.keys(PLATFORM_LABEL) as Platform[];

/** Plain-language reasons, with what to do when the user can fix it. */
const REASONS: Record<ExitReason, string> = {
  stopped_by_user: "Stopped by you.",
  scheduled_end: "Reached its scheduled end time.",
  entitlement_lost: "Stopped because the workspace no longer has an active plan.",
  completed: "Played the playlist once and finished.",
  crashed: "The stream process crashed. It retries on its own.",
  stalled: "The stream stopped sending data. It retries on its own.",
  input_unreachable: "A video file couldn't be read. It retries on its own.",
  startup_timeout: "The platform didn't accept the stream in time. It retries on its own.",
  worker_lost: "The server running it went away. It moves to another server on its own.",
  platform_session_limit: "The platform's maximum broadcast length was reached; it restarts on its own.",
  fatal_auth: "The platform refused the stream key.",
  fatal_config: "The platform rejected the stream settings. Check the RTMP address and quality.",
  key_refused: "The platform refused the stream key.",
};

/** Where to fix a refused key on each platform, so the advice matches the stream's own platform. */
const KEY_ADVICE: Record<Platform, string> = {
  youtube: "Check the stream key in YouTube Studio, then start again.",
  facebook:
    "Ending the live video on Facebook also ends its stream key, unless it is a persistent key. Copy a new key (or turn on a persistent key) in Facebook Live Producer, then start again.",
  twitch: "Check the stream key in your Twitch Creator Dashboard, then start again.",
  custom_rtmp: "Check the stream key and RTMP address with your provider, then start again.",
};
const KEY_ADVICE_ANY = "Check the stream key on the platform, then start again.";

/** The reason as a sentence; give the stream's platform so a refused key comes with that platform's fix. */
export function exitReasonText(r: ExitReason | null | undefined, platform?: Platform): string | null {
  if (!r) return null;
  const text = REASONS[r] ?? r;
  if (r !== "key_refused" && r !== "fatal_auth") return text;
  return `${text} ${platform ? KEY_ADVICE[platform] : KEY_ADVICE_ANY}`;
}

/**
 * The raw error line (ffmpeg's last stderr lines) cleaned for display: the `[out#0/flv @ 0x…]` prefixes are dropped and
 * repeated lines shown once. Shown even for a refused key: the reason is inferred from this text, so it is the
 * evidence when the guess is wrong (Facebook's "Unknown error occurred" can also be its ingest having trouble).
 */
export function errorDetailText(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const lines = raw.split(" | ").map((l) => l.replace(/^\[[^\]]*@ 0x[0-9a-f]+\]\s*/i, "").trim()).filter(Boolean);
  return [...new Set(lines)].join("\n") || null;
}

/** Milliseconds a running stream has been up, or null when it isn't up. */
export function uptimeMs(s: { actualState: ActualState; runStartedAt: string | null }, now: number): number | null {
  if (s.actualState !== "RUNNING" || !s.runStartedAt) return null;
  const t = Date.parse(s.runStartedAt);
  return Number.isNaN(t) ? null : Math.max(0, now - t);
}
